import { miniappApi } from '@/api/miniapp/miniappApi';
import type { AiChatMessage, AiChatOperation } from '@/api/miniapp/types';

/**
 * Задачи ассистента: предпросмотр сдачи и финальный ответ.
 *
 * Ручка возвращает только принятую задачу, а результат приходит в историю
 * отдельной операцией — поэтому ожидание описано здесь, отдельно от состояния
 * переписки.
 */

/**
 * Отпечаток набора готовых операций.
 *
 * Появление результата определяется по новой готовой операции, а не по
 * совпадению с `run_id` из ответа на запрос: у операций в истории свои
 * идентификаторы, и ожидание конкретного `run_id` может не завершиться никогда.
 */
function readyKeys(operations: AiChatOperation[], kind: string): Set<string> {
  return new Set(
    operations
      .filter((operation) => operation.kind === kind && operation.status === 'ready')
      .map((operation) => `${operation.run_id}:${operation.created_at ?? ''}`)
  );
}


/**
 * Ожидание результата поставленной задачи.
 *
 * Итог приходит в историю чата отдельной операцией со статусом `ready`, а не
 * сообщением и не в ответе на сам запрос. История перечитывается, пока не
 * появится новая готовая операция нужного вида.
 *
 * Ожидание ограничено минутой: дольше держать пользователя перед неподвижным
 * экраном нельзя. Работа на сервере при этом не прерывается — результат
 * подхватится следующей загрузкой истории.
 */
const RUN_POLL_MS = 3000;
const RUN_TIMEOUT_MS = 60000;

/**
 * Чем закончилась сборка итога.
 *
 * `answered` — агент ответил текстом вместо итога: обычно ему не хватает
 * ответов, и он задаёт уточняющие вопросы. Ждать файл дальше бессмысленно, а
 * экран «собираю итог» поверх его вопросов вводит в заблуждение.
 */
export type PreviewOutcome = 'ready' | 'answered' | 'timeout';

async function waitForRun(
  reload: () => Promise<void>,
  check: () => PreviewOutcome | null
): Promise<PreviewOutcome> {
  const deadline = Date.now() + RUN_TIMEOUT_MS;

  while (Date.now() < deadline) {
    // Проверка до паузы: ответ агента по сокету мог прийти, пока шёл запрос
    const early = check();
    if (early) return early;

    await new Promise((resolve) => setTimeout(resolve, RUN_POLL_MS));
    // Пока канал ответов жив, историю запрашивать незачем: всё, что делает
    // агент, приходит по нему. Опрос остаётся только запасным путём
    await reload();
    const outcome = check();
    if (outcome) return outcome;
  }
  return 'timeout';
}

/** Появился ли новый готовый итог по сравнению с прежним состоянием. */
export function hasNewReadyOperation(before: AiChatOperation[], after: AiChatOperation[]): boolean {
  const ready = (list: AiChatOperation[]) => list.filter((item) => item.status === 'ready').length;
  return ready(after) > ready(before);
}

/** Появился ли в истории новый ответ ассистента по сравнению с прежней. */
export function hasNewAssistantReply(before: AiChatMessage[], after: AiChatMessage[]): boolean {
  const count = (list: AiChatMessage[]) => list.filter((message) => message.role === 'assistant').length;
  return count(after) > count(before);
}

/** Есть ли уже собранный итог, который можно отправлять. */
export function hasReadyPreview(operations: AiChatOperation[]): boolean {
  return operations.some(
    (operation) => operation.kind === 'submission_preview' && operation.status === 'ready'
  );
}

/** То, что нужно ожиданию итога от состояния чата. */
export interface PreviewContext {
  operations(): AiChatOperation[];
  messages(): AiChatMessage[];
  artifactSeenAt(): number;
  socketAlive(): boolean;
  reloadHistory(): Promise<void>;
}

/** Ставит сборку итога и ждёт, чем она закончится. */
export async function preparePreview(context: PreviewContext): Promise<PreviewOutcome> {
  const before = readyKeys(context.operations(), 'submission_preview');
  const seenBefore = context.artifactSeenAt();
  const messagesBefore = context.messages();

  await miniappApi.submissionPreview();

  return waitForRun(
    async () => {
      // Готовый материал приходит по сокету; история нужна только когда его нет
      if (!context.socketAlive()) await context.reloadHistory();
    },
    () => {
      if (context.artifactSeenAt() !== seenBefore) return 'ready';
      const now = readyKeys(context.operations(), 'submission_preview');
      if ([...now].some((key) => !before.has(key))) return 'ready';

      // Агент ответил текстом, а не файлом: итога не будет, он просит уточнений
      if (hasNewAssistantReply(messagesBefore, context.messages())) return 'answered';
      return null;
    }
  );
}
